import { useCallback, useMemo } from 'react';
import { useImportStore } from '../store/importStore';
import { DetectedAssetType } from '../../core/types/import.types';

export function useAssetImport() {
  const {
    files,
    settings,
    isImporting,
    progress,
    results,
    isOpen,
    selectedFileId,
    sortBy,
    sortOrder,
    openDialog,
    closeDialog,
    addFiles,
    removeFile,
    clearFiles,
    selectFile,
    toggleFileSelected,
    selectAll,
    deselectAll,
    updateFile,
    updateSettings,
    resetSettings,
    startImport,
    cancelImport,
    retryFailed,
    setSortBy,
    setSortOrder,
  } = useImportStore();

  // Sorted files
  const sortedFiles = useMemo(() => {
    const sorted = [...files];
    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
        case 'size':
          comparison = a.size - b.size;
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [files, sortBy, sortOrder]);

  // Selected file
  const selectedFile = useMemo(() => {
    return files.find(f => f.id === selectedFileId) || null;
  }, [files, selectedFileId]);

  // Statistics
  const stats = useMemo(() => {
    const selected = files.filter(f => f.isSelected);
    const byType: Record<DetectedAssetType, number> = {
      image: 0,
      audio: 0,
      script: 0,
      font: 0,
      json: 0,
      spine: 0,
      spritesheet: 0,
      unknown: 0,
    };

    let totalSize = 0;

    for (const file of selected) {
      byType[file.type] = (byType[file.type] || 0) + 1;
      totalSize += file.size;
    }

    return {
      total: files.length,
      selected: selected.length,
      byType,
      totalSize,
      pending: files.filter(f => f.status === 'pending').length,
      completed: files.filter(f => f.status === 'complete').length,
      failed: files.filter(f => f.status === 'error').length,
    };
  }, [files]);

  // Handle drag and drop
  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const items = e.dataTransfer?.items;
    if (!items) return;

    const fileList: File[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          fileList.push(file);
        }
      }
    }

    if (fileList.length > 0) {
      addFiles(fileList);
    }
  }, [addFiles]);

  // Handle file input change
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      // Reset input so same files can be selected again
      e.target.value = '';
    }
  }, [addFiles]);

  // Validation
  const validation = useMemo(() => {
    const errors: string[] = [];

    if (files.length === 0) {
      errors.push('No files selected');
    }

    const selectedCount = files.filter(f => f.isSelected).length;
    if (selectedCount === 0 && files.length > 0) {
      errors.push('No files checked for import');
    }

    // Check for duplicate names in same folder
    const namesByFolder: Record<string, string[]> = {};
    for (const file of files.filter(f => f.isSelected)) {
      const folder = file.targetFolder;
      const name = file.customName || file.name;
      if (!namesByFolder[folder]) {
        namesByFolder[folder] = [];
      }
      if (namesByFolder[folder].includes(name)) {
        errors.push(`Duplicate file name: ${name}`);
      } else {
        namesByFolder[folder].push(name);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, [files]);

  // Can import check
  const canImport = useMemo(() => {
    return validation.isValid && !isImporting && stats.selected > 0;
  }, [validation.isValid, isImporting, stats.selected]);

  return {
    // State
    files: sortedFiles,
    settings,
    isImporting,
    progress,
    results,
    isOpen,
    selectedFile,
    sortBy,
    sortOrder,
    stats,
    validation,
    canImport,

    // Actions
    openDialog,
    closeDialog,
    addFiles,
    removeFile,
    clearFiles,
    selectFile,
    toggleFileSelected,
    selectAll,
    deselectAll,
    updateFile,
    updateSettings,
    resetSettings,
    startImport,
    cancelImport,
    retryFailed,
    setSortBy,
    setSortOrder,
    handleDrop,
    handleFileInputChange,
  };
}
